import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const RegistrationForm = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    businessName: '',
    email: '',
    password: '',
    confirmPassword: '',
    businessType: '',
    agreeToTerms: false
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const businessTypes = [
    { value: 'hall', label: 'Аренда залов' },
    { value: 'office', label: 'Офисные помещения' },
    { value: 'coworking', label: 'Коворкинг' },
    { value: 'workstation', label: 'Индивидуальные рабочие места' }
  ];

  const validateField = (name, value) => {
    switch (name) {
      case 'businessName':
        if (!value?.trim()) return 'Название бизнеса обязательно';
        if (value?.trim()?.length < 3) return 'Минимум 3 символа';
        return '';

      case 'email':
        if (!value?.trim()) return 'Email обязателен';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex?.test(value)) return 'Некорректный формат email';
        return '';

      case 'password':
        if (!value) return 'Пароль обязателен';
        if (value?.length < 8) return 'Минимум 8 символов';
        if (!/(?=.*[a-z])/?.test(value)) return 'Должна быть строчная буква';
        if (!/(?=.*[A-Z])/?.test(value)) return 'Должна быть заглавная буква';
        if (!/(?=.*\d)/?.test(value)) return 'Должна быть цифра';
        return '';

      case 'confirmPassword':
        if (!value) return 'Подтверждение пароля обязательно';
        if (value !== formData?.password) return 'Пароли не совпадают';
        return '';

      case 'businessType':
        if (!value) return 'Выберите тип бизнеса';
        return '';

      case 'agreeToTerms':
        if (!value) return 'Необходимо согласие с условиями';
        return '';

      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: fieldValue
    }));

    if (touched?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: validateField(name, fieldValue)
      }));
    }

    if (name === 'password' && touched?.confirmPassword && formData?.confirmPassword) {
      setErrors(prev => ({
        ...prev,
        confirmPassword: formData?.confirmPassword !== value ? 'Пароли не совпадают' : ''
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value, type, checked } = e?.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    setErrors(prev => ({
      ...prev,
      [name]: validateField(name, fieldValue)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData)?.forEach(key => {
      const error = validateField(key, formData?.[key]);
      if (error) newErrors[key] = error;
    });
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitError('');

    const newErrors = validateForm();
    setErrors(newErrors);
    setTouched({
      businessName: true,
      email: true,
      password: true,
      confirmPassword: true,
      businessType: true,
      agreeToTerms: true
    });

    if (Object.keys(newErrors)?.length > 0) {
      return;
    }

    setIsLoading(true);

    try {
      await register(formData.email, formData.password, formData.businessName);
      navigate('/template-selection');
    } catch (error) {
      let message = 'Ошибка регистрации. Попробуйте снова.';
      if (error.code === 'auth/email-already-in-use') {
        message = 'Этот email уже зарегистрирован';
      } else if (error.code === 'auth/weak-password') {
        message = 'Пароль слишком слабый';
      }
      setSubmitError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
      <Input
        label="Название бизнеса"
        type="text"
        name="businessName"
        placeholder="Например: Коворкинг Центр"
        value={formData?.businessName}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched?.businessName ? errors?.businessName : ''}
        required
      />
      <Input
        label="Email"
        type="email"
        name="email"
        placeholder="your@email.ru"
        value={formData?.email}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched?.email ? errors?.email : ''}
        required
      />
      <div className="space-y-4">
        <Input
          label="Пароль"
          type="password"
          name="password"
          placeholder="Минимум 8 символов"
          value={formData?.password}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched?.password ? errors?.password : ''}
          description="Должен содержать заглавные и строчные буквы, цифры"
          required
        />

        <Input
          label="Подтвердите пароль"
          type="password"
          name="confirmPassword"
          placeholder="Повторите пароль"
          value={formData?.confirmPassword}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched?.confirmPassword ? errors?.confirmPassword : ''}
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Тип бизнеса <span className="text-error">*</span>
        </label>
        <select
          name="businessType"
          value={formData?.businessType}
          onChange={handleChange}
          onBlur={handleBlur}
          className="w-full px-4 py-2.5 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
        >
          <option value="">Выберите тип</option>
          {businessTypes?.map(type => (
            <option key={type?.value} value={type?.value}>
              {type?.label}
            </option>
          ))}
        </select>
        {touched?.businessType && errors?.businessType && (
          <p className="mt-1.5 text-sm text-error">{errors?.businessType}</p>
        )}
      </div>
      <Checkbox
        name="agreeToTerms"
        label="Я согласен с условиями использования и политикой конфиденциальности"
        checked={formData?.agreeToTerms}
        onChange={handleChange}
        error={touched?.agreeToTerms ? errors?.agreeToTerms : ''}
        required
      />
      {submitError && (
        <div className="p-3 bg-error/10 border border-error/20 rounded-lg">
          <p className="text-sm text-error">{submitError}</p>
        </div>
      )}
      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isLoading}
        iconName="UserPlus"
        iconPosition="left"
      >
        {isLoading ? 'Создание аккаунта...' : 'Создать аккаунт'}
      </Button>
    </form>
  );
};

export default RegistrationForm;